import React, { useEffect, useState } from "react";
import { api, ROLE_LABELS } from "@/lib/api";
import ServiceList from "@/pages/services-admin/ServiceList";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PricingLogicEditor } from "@/components/PricingLogicEditor";
import { SkipVisitPricingFields, ProPicker } from "@/components/SkipVisitPricingFields";
import { PricingStatsDialog } from "@/components/PricingStatsDialog";
import CustomActionsEditor from "@/components/CustomActionsEditor";
import PaymentMethodsAdmin from "@/pages/services-admin/PaymentMethodsAdmin";
import { Plus, Pencil, Trash, FileText, House, Buildings, Certificate, ClipboardText, Lightning, Ruler, MagnifyingGlass, MapPin, Camera, Wrench, Hammer, PaintBrush, Drop, Fire, Tree, Sun, ThermometerHot, Key, Handshake, Money, Receipt, Bank, ShieldCheck, Warning, CheckCircle, User, Users, Package, Truck, Calendar, ArrowsOutCardinal, CaretUp, CaretDown, DotsSixVertical } from "@phosphor-icons/react";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Icone disponibili per i servizi
const ICON_MAP = {
  FileText, House, Buildings, Certificate, ClipboardText, Lightning, Ruler,
  MagnifyingGlass, MapPin, Camera, Wrench, Hammer, PaintBrush, Drop, Fire,
  Tree, Sun, ThermometerHot, Key, Handshake, Money, Receipt, Bank,
  ShieldCheck, Warning, CheckCircle, User, Users, Package, Truck, Calendar,
};
const ICON_OPTIONS = Object.keys(ICON_MAP);

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

/**
 * UnifiedRequirementsPicker (Iter79) — Gestore unico di "Requisiti" del servizio.
 * Combina in una singola lista ordinata i requisiti tipo `document` (dal catalogo
 * globale) e `service` (altri servizi concatenati che il PRO deve avere o comprare).
 * Sincronizza automaticamente `form.chained_service_ids` e `form.required_document_ids`
 * per la retro-compat lato backend/UI acquisto.
 */
function UnifiedRequirementsPicker({ form, setForm, services, documentCatalog, editingId, onDocCatalogReload }) {
  const requirements = form.requirements || [];
  const [addType, setAddType] = React.useState("document");
  const [addId, setAddId] = React.useState("");
  // Iter208.50 — Quick-create documento senza uscire dal form.
  const [quickDocOpen, setQuickDocOpen] = React.useState(false);
  const [quickDocName, setQuickDocName] = React.useState("");
  const [quickDocDesc, setQuickDocDesc] = React.useState("");
  const [quickDocSaving, setQuickDocSaving] = React.useState(false);

  const syncLegacyFields = (reqs) => {
    const svc_ids = reqs.filter(r => r.type === "service").map(r => r.id);
    const doc_ids = reqs.filter(r => r.type === "document").map(r => r.id);
    setForm(prev => ({
      ...prev,
      requirements: reqs,
      chained_service_ids: svc_ids,
      required_document_ids: doc_ids,
    }));
  };

  const addReq = () => {
    if (!addId) return;
    if (requirements.some(r => r.type === addType && r.id === addId)) {
      setAddId(""); return;
    }
    syncLegacyFields([...requirements, { type: addType, id: addId }]);
    setAddId("");
  };
  const removeReq = (idx) => syncLegacyFields(requirements.filter((_, i) => i !== idx));

  const availableDocs = documentCatalog.filter(d =>
    d.is_active && !requirements.some(r => r.type === "document" && r.id === d._id)
  );
  const availableServices = services.filter(s =>
    s._id !== editingId && !requirements.some(r => r.type === "service" && r.id === s._id)
  );

  const labelFor = (r) => {
    if (r.type === "document") {
      const d = documentCatalog.find(x => x._id === r.id);
      return d ? d.name : `(documento rimosso: ${r.id})`;
    }
    const s = services.find(x => x._id === r.id);
    return s ? `${s.name} · € ${Number(s.base_price || 0).toFixed(2)}` : `(servizio rimosso: ${r.id})`;
  };

  return (
    <div className="p-3 rounded-md border" data-testid="unified-requirements-picker">
      <div className="text-sm font-semibold text-slate-900 mb-1">📋 Requisiti al momento dell'acquisto</div>
      <p className="text-xs text-slate-600 mb-3">
        Unica lista con documenti da spuntare e servizi prerequisiti (che il PRO deve avere o acquistare in blocco). L'ordine è preservato nella schermata di acquisto.
      </p>
      {requirements.length > 0 ? (
        <ul className="space-y-1.5 mb-3" data-testid="requirements-list">
          {requirements.map((r, i) => (
            <li key={`${r.type}-${r.id}-${i}`} className="flex items-center gap-2 p-2 rounded border bg-white" data-testid={`req-item-${i}`}>
              <span className={`text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${r.type === "document" ? "bg-purple-100 text-purple-800" : "bg-emerald-100 text-emerald-800"}`}>
                {r.type === "document" ? "📄 Doc" : "🔗 Servizio"}
              </span>
              <span className="flex-1 text-sm">{labelFor(r)}</span>
              <button
                type="button"
                onClick={() => removeReq(i)}
                className="text-red-600 text-xs hover:text-red-800"
                data-testid={`req-remove-${i}`}
                aria-label="Rimuovi requisito"
              >✕</button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-xs italic text-muted-foreground py-2 mb-2">Nessun requisito impostato.</div>
      )}
      <div className="flex gap-2 flex-wrap items-center">
        <select
          value={addType}
          onChange={e => { setAddType(e.target.value); setAddId(""); }}
          className="text-sm h-9 border rounded px-2"
          data-testid="req-add-type"
        >
          <option value="document">📄 Documento</option>
          <option value="service">🔗 Servizio</option>
        </select>
        <select
          value={addId}
          onChange={e => setAddId(e.target.value)}
          className="text-sm h-9 border rounded px-2 flex-1 min-w-[200px]"
          data-testid="req-add-id"
        >
          <option value="">— Seleziona —</option>
          {addType === "document"
            ? availableDocs.map(d => <option key={d._id} value={d._id}>{d.name}</option>)
            : availableServices.map(s => <option key={s._id} value={s._id}>{s.name} · €{Number(s.base_price||0).toFixed(2)}</option>)}
        </select>
        <Button
          type="button"
          size="sm"
          onClick={addReq}
          disabled={!addId}
          className="ht-gradient border-0"
          data-testid="req-add-btn"
        >+ Aggiungi</Button>
        {addType === "document" && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => { setQuickDocOpen(v => !v); setQuickDocName(""); setQuickDocDesc(""); }}
            className="text-xs"
            data-testid="req-quick-doc-toggle"
          >+ Nuovo documento</Button>
        )}
      </div>
      {quickDocOpen && addType === "document" && (
        <div className="mt-3 p-3 rounded-md border bg-purple-50/40 space-y-2" data-testid="req-quick-doc-panel">
          <div className="text-xs font-semibold text-slate-700">Crea un nuovo tipo di documento nel Catalogo</div>
          <Input
            placeholder="Nome documento (es. Certificato di residenza)"
            value={quickDocName}
            onChange={(e) => setQuickDocName(e.target.value)}
            data-testid="req-quick-doc-name"
          />
          <Input
            placeholder="Descrizione (opzionale)"
            value={quickDocDesc}
            onChange={(e) => setQuickDocDesc(e.target.value)}
            data-testid="req-quick-doc-desc"
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setQuickDocOpen(false)}
              data-testid="req-quick-doc-cancel"
            >Annulla</Button>
            <Button
              type="button"
              size="sm"
              className="ht-gradient border-0"
              disabled={quickDocSaving || !quickDocName.trim()}
              onClick={async () => {
                setQuickDocSaving(true);
                try {
                  const r = await api.post("/document-catalog", {
                    name: quickDocName.trim(),
                    description: quickDocDesc.trim(),
                    is_active: true,
                  });
                  const newDoc = r.data;
                  // Ricarica catalog globale
                  await onDocCatalogReload?.();
                  // Attacca subito al servizio
                  syncLegacyFields([...requirements, { type: "document", id: newDoc._id }]);
                  toast.success("Documento creato e allegato");
                  setQuickDocOpen(false);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Errore creazione documento");
                } finally {
                  setQuickDocSaving(false);
                }
              }}
              data-testid="req-quick-doc-save"
            >{quickDocSaving ? "…" : "Crea e allega"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ServicesAdmin() {
  const [services, setServices] = useState([]);
  const [documentCatalog, setDocumentCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statsService, setStatsService] = useState(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", slug: "", description: "", client_info: "", category: "generico", base_price: 0,
    workflow_id: "", is_active: true, icon: "FileText", delivery_time_days: 7,
    requires_visit: false, visit_duration_minutes: 60, allowed_category_ids: [],
    keywords: [], chained_service_ids: [], fixed_price: false,
    allow_secondary_visit: false,
    secondary_visit_form_schema: null,
    allow_multiple_quantity: false,
    quantity_discount_tiers: [],
    has_questionnaire: false,
    question_text: "",
    questionnaire_fields: [],
    can_skip_visit: false,
    skip_visit_note: "",
    skip_visit_pricing_enabled: false,
    skip_visit_price_skip: 0,
    skip_visit_price_no_skip: 0,
    skip_visit_pricing_scope: "all",
    skip_visit_pricing_professionals: [],
    required_document_ids: [],
    has_flexible_price: false,
    pricing_logic: [],
    // Iter140p — Split pagamenti anticipo/saldo
    split_payment_enabled: false,
    advance_percentage: 50,
  });
  const [keywordInput, setKeywordInput] = useState("");
  const [operatorScores, setOperatorScores] = useState([]);  // [{operator_id, first_name, ..., score, compensation}]
  const [workflowSteps, setWorkflowSteps] = useState([]);    // steps del workflow associato al servizio
  const [selectedStepIdx, setSelectedStepIdx] = useState(null);
  // Iter207.8 — Modale di conferma per disattivazione sopralluogo secondario con dati
  const [confirmDisableSecondary, setConfirmDisableSecondary] = useState(false);

  // Iter207.8 — Un form ha "contenuto" se almeno una sezione contiene domande
  const hasSecondaryContent = (schema) => {
    if (!schema) return false;
    return (schema.sections || []).some((sec) => (sec.questions || []).length > 0);
  };

  const reloadDocCatalog = async () => {
    const d = await api.get("/document-catalog").catch(() => ({ data: [] }));
    setDocumentCatalog(d.data || []);
  };

  const load = async () => {
    const [s, c, d] = await Promise.all([
      api.get("/services"),
      api.get("/professional-categories"),
      api.get("/document-catalog").catch(() => ({ data: [] })),
    ]);
    setServices(s.data);
    setCategories(c.data);
    setDocumentCatalog(d.data || []);
  };
  useEffect(() => { load(); }, []);

  // Load operator list (score 0 by default for new service)
  const loadOperatorScores = async (serviceId = null) => {
    // For a NEW service pass a placeholder id — endpoint returns operators with score=0/comp=0
    const sid = serviceId || "__new__";
    const r = await api.get(`/services/${sid}/operator-scores`);
    setOperatorScores(r.data || []);
  };

  // Load workflow steps for editing a service
  const loadWorkflowSteps = async (serviceId) => {
    if (!serviceId) { setWorkflowSteps([]); return; }
    try {
      const r = await api.get(`/services/${serviceId}/workflow`);
      setWorkflowSteps(r.data?.steps || []);
    } catch (err) { setWorkflowSteps([]); }
  };

  const open = async (s = null) => {
    // Ricarico categorie/servizi per avere la lista fresca
    const cRes = await api.get("/professional-categories");
    const freshCategories = cRes.data || [];
    setCategories(freshCategories);

    setEditing(s);
    // Iter79: derive `requirements` dalla concatenazione di documenti + servizi
    // se il servizio in edit non ha già il campo popolato (retro-compat).
    const initReqs = (s) => {
      if (Array.isArray(s?.requirements) && s.requirements.length > 0) return s.requirements;
      return [
        ...((s?.required_document_ids) || []).map(id => ({ type: "document", id })),
        ...((s?.chained_service_ids) || []).map(id => ({ type: "service", id })),
      ];
    };
    setForm(s ? { ...s, has_signature_requests: !!s.has_signature_requests, visibility_scope: s.visibility_scope || "all", hidden_professional_ids: s.hidden_professional_ids || [], client_info: s.client_info || "", allowed_category_ids: s.allowed_category_ids || [], keywords: s.keywords || [], chained_service_ids: s.chained_service_ids || [], fixed_price: !!s.fixed_price, allow_secondary_visit: !!s.allow_secondary_visit, secondary_visit_form_schema: s.secondary_visit_form_schema || null, allow_multiple_quantity: !!s.allow_multiple_quantity, quantity_discount_tiers: s.quantity_discount_tiers || [], has_questionnaire: !!s.has_questionnaire, question_text: s.question_text || "", questionnaire_fields: s.questionnaire_fields || [], can_skip_visit: !!s.can_skip_visit, skip_visit_note: s.skip_visit_note || "", skip_visit_pricing_enabled: !!s.skip_visit_pricing_enabled, skip_visit_price_skip: s.skip_visit_price_skip ?? 0, skip_visit_price_no_skip: s.skip_visit_price_no_skip ?? 0, skip_visit_pricing_scope: s.skip_visit_pricing_scope || "all", skip_visit_pricing_professionals: s.skip_visit_pricing_professionals || [], required_document_ids: s.required_document_ids || [], requirements: initReqs(s), has_flexible_price: !!s.has_flexible_price, pricing_logic: s.pricing_logic || [], split_payment_enabled: !!s.split_payment_enabled, advance_percentage: Number.isFinite(s.advance_percentage) ? s.advance_percentage : 50, custom_actions: s.custom_actions || [], visit_booking_buffer_hours: Number.isFinite(s.visit_booking_buffer_hours) ? s.visit_booking_buffer_hours : 0 } : {
      name: "", slug: "", description: "", client_info: "", category: "generico", base_price: 0,
      workflow_id: "", is_active: true, icon: "FileText", delivery_time_days: 7,
      requires_visit: false, visit_duration_minutes: 60, allowed_category_ids: [], keywords: [], chained_service_ids: [], fixed_price: false, has_signature_requests: false, visibility_scope: "all", hidden_professional_ids: [],
      allow_secondary_visit: false, secondary_visit_form_schema: null, allow_multiple_quantity: false, quantity_discount_tiers: [], has_questionnaire: false, question_text: "", questionnaire_fields: [], can_skip_visit: false, skip_visit_note: "", skip_visit_pricing_enabled: false, skip_visit_price_skip: 0, skip_visit_price_no_skip: 0, skip_visit_pricing_scope: "all", skip_visit_pricing_professionals: [], required_document_ids: [], requirements: [], has_flexible_price: false, pricing_logic: [], split_payment_enabled: false, advance_percentage: 50, custom_actions: [],
    });
    setKeywordInput("");
    setOperatorScores([]);
    setWorkflowSteps([]);
    setSelectedStepIdx(null);
    setDialogOpen(true);
    // Load operators + workflow (async)
    loadOperatorScores(s?._id);
    loadWorkflowSteps(s?._id);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setOperatorScores([]);
    setWorkflowSteps([]);
    setSelectedStepIdx(null);
  };

  const toggleCategory = (cid) => {
    setForm(f => ({
      ...f,
      allowed_category_ids: f.allowed_category_ids.includes(cid)
        ? f.allowed_category_ids.filter(x => x !== cid)
        : [...f.allowed_category_ids, cid],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    // Iter128: se il Prezzo Flessibile è attivo, non permettere il salvataggio
    // con domande a testo vuoto (es. `q_1785850925735_7081`) — impedisce
    // che il cliente veda box senza descrizione.
    if (form.has_flexible_price) {
      const empty = (form.pricing_logic || []).filter(q => !(q.question || "").trim());
      if (empty.length > 0) {
        toast.error(`Ci sono ${empty.length} domanda/e senza testo nel Prezzo Flessibile. Compilale o rimuovile prima di salvare.`);
        return;
      }
    }
    try {
      // Genera transitions lineari dagli step ordinati
      const orderedSteps = workflowSteps
        .map((s, i) => ({ ...s, order: i + 1 }))
        .map(s => ({
          id: s.id || (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)),
          name: s.name || "Nuovo step",
          assigned_role: s.assigned_role || "desk_operator",
          order: s.order,
          is_final: !!s.is_final,
          is_visit: !!s.is_visit,
          is_entity_fees_step: !!s.is_entity_fees_step,
          requires_supervision: !!s.requires_supervision,
          has_form: !!(s.is_visit && s.has_form),  // consistency: no form without visit
          form_schema: (s.is_visit && s.has_form) ? (s.form_schema || null) : null,
          visit_duration_minutes: Number(s.visit_duration_minutes || 60),
          description: s.description || "",
          position_x: Number(s.position_x || 0),
          position_y: Number(s.position_y || 0),
          // Iter129 — Modalità Assegnazione: `auto` per default. Se
          // `same_as` ma `same_as_step_id` è vuoto, ricadiamo su `auto`
          // per evitare stato inconsistente in DB.
          assignment_mode: (s.assignment_mode === "same_as" && s.same_as_step_id) ? "same_as" : "auto",
          same_as_step_id: (s.assignment_mode === "same_as" && s.same_as_step_id) ? s.same_as_step_id : null,
        }));
      // Auto-deriva has_entity_fees dal fatto che almeno uno step lo gestisce.
      // Mantiene retrocompatibilità del filtro service-level senza chiedere due click all'admin.
      const hasEntityFees = orderedSteps.some(s => s.is_entity_fees_step);
      const transitions = [];
      for (let i = 0; i < orderedSteps.length - 1; i++) {
        transitions.push({ from: orderedSteps[i].id, to: orderedSteps[i + 1].id, label: "avanti" });
      }
      const payload = {
        ...form,
        has_entity_fees: hasEntityFees,
        fixed_price: !!form.fixed_price,
        has_signature_requests: !!form.has_signature_requests,
        allow_secondary_visit: !!form.allow_secondary_visit,
        secondary_visit_form_schema: form.allow_secondary_visit ? (form.secondary_visit_form_schema || null) : null,
        allow_multiple_quantity: !!form.allow_multiple_quantity,
        quantity_discount_tiers: form.allow_multiple_quantity
          ? (form.quantity_discount_tiers || [])
              .filter(t => Number(t.min_qty) >= 2 && Number(t.discount_pct) > 0)
              .map(t => ({ min_qty: Number(t.min_qty), discount_pct: Number(t.discount_pct) }))
          : [],
        has_questionnaire: !!form.has_questionnaire,
        question_text: form.has_questionnaire ? (form.question_text || "").trim() : "",
        questionnaire_fields: form.has_questionnaire ? (form.questionnaire_fields || []) : [],
        can_skip_visit: !!form.can_skip_visit,
        skip_visit_note: form.can_skip_visit ? (form.skip_visit_note || "").trim() : "",
        skip_visit_pricing_enabled: !!form.can_skip_visit && !!form.skip_visit_pricing_enabled,
        skip_visit_price_skip: Number(form.skip_visit_price_skip) || 0,
        skip_visit_price_no_skip: Number(form.skip_visit_price_no_skip) || 0,
        skip_visit_pricing_scope: form.skip_visit_pricing_scope === "selected" ? "selected" : "all",
        skip_visit_pricing_professionals: form.skip_visit_pricing_scope === "selected" ? (form.skip_visit_pricing_professionals || []) : [],
        required_document_ids: form.required_document_ids || [],
        has_flexible_price: !!form.has_flexible_price,
        pricing_logic: form.has_flexible_price ? (form.pricing_logic || []) : [],
        // Iter140p — Split anticipo/saldo
        split_payment_enabled: !!form.split_payment_enabled,
        advance_percentage: form.split_payment_enabled
          ? Math.max(1, Math.min(99, Number(form.advance_percentage) || 50))
          : 0,
        base_price: Number(form.base_price),
        delivery_time_days: Number(form.delivery_time_days),
        visit_duration_minutes: Number(form.visit_duration_minutes) || 60,
        visit_booking_buffer_hours: Math.max(0, Number(form.visit_booking_buffer_hours) || 0),
        chained_service_ids: form.chained_service_ids || [],
        // Iter79: lista unificata dei requisiti
        requirements: form.requirements || [],
        // Iter208.68 — Azioni personalizzate ("Azioni per il Pro")
        custom_actions: (form.custom_actions || []).map((a) => ({
          id: a.id,
          name: (a.name || "").trim(),
          visible_on_step: a.visible_on_step || "all",
          confirmation_text: (a.confirmation_text || "").trim(),
          extra_emails: (a.extra_emails || []).map((e) => (e || "").trim()).filter(Boolean),
          blocks: (a.blocks || []).map((b) => ({
            id: b.id,
            type: b.type,
            label: (b.label || "").trim(),
            required: !!b.required,
            ...(b.type === "calendar_slots"
              ? { slots: (b.slots || []).filter((s) => s.date && s.start && s.end) }
              : {}),
            ...(b.type === "file_upload"
              ? { max_files: Number(b.max_files) || 5 }
              : {}),
          })),
        })),
        workflow_steps: orderedSteps,
        workflow_transitions: transitions,
      };
      let sid;
      if (editing) {
        await api.patch(`/services/${editing._id}`, payload);
        sid = editing._id;
      } else {
        const r = await api.post("/services", payload);
        sid = r.data._id || r.data.id;
      }
      // Save operator scores/compensations
      if (sid && operatorScores.length > 0) {
        const items = operatorScores.map(o => ({
          operator_id: o.operator_id,
          score: parseInt(o.score) || 0,
          compensation: parseFloat(o.compensation) || 0,
          compensation_type: o.compensation_type === "pct" ? "pct" : "eur",
        }));
        await api.put(`/services/${sid}/operator-scores`, { items });
      }
      toast.success("Servizio salvato");
      closeDialog();
      setForm({ name: "", slug: "", description: "", client_info: "", category: "generico", base_price: 0, workflow_id: "", is_active: true, icon: "FileText", delivery_time_days: 7, requires_visit: false, visit_duration_minutes: 60, allowed_category_ids: [], keywords: [] });
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x?.msg || String(x)).join("; ") : (d || "Errore"));
    }
  };

  const del = async (id) => {
    if (!confirm("Eliminare?")) return;
    await api.delete(`/services/${id}`);
    load();
  };

  return (
    <div>
      <div className="mb-6">
        <div className="label-eyebrow">Configurazione</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Servizi</h1>
        <p className="text-muted-foreground mt-2">Gestisci il catalogo del marketplace e il flusso di lavorazione di ogni servizio</p>
      </div>

      <Tabs defaultValue="services" className="space-y-4">
        <TabsList data-testid="services-admin-tabs">
          <TabsTrigger value="services" data-testid="tab-services">Servizi</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Pagamenti</TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="space-y-4">
          <div className="flex items-end justify-end mb-4">
            <Button className="ht-gradient border-0" onClick={() => open()} data-testid="new-service-btn">
              <Plus size={16} className="mr-2" weight="bold" /> Nuovo Servizio
            </Button>
          </div>

          <ServiceList
            services={services}
            onEdit={open}
            onDelete={del}
            onStats={(s) => { setStatsService(s); setStatsOpen(true); }}
          />
        </TabsContent>

        <TabsContent value="payments">
          <PaymentMethodsAdmin />
        </TabsContent>
      </Tabs>

      <PricingStatsDialog
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        service={statsService}
      />

      {/* Iter207.8 — Conferma disattivazione sopralluogo secondario con dati compilati */}
      <Dialog open={confirmDisableSecondary} onOpenChange={(o) => { if (!o) setConfirmDisableSecondary(false); }}>
        <DialogContent className="max-w-md" data-testid="confirm-disable-secondary-dialog">
          <DialogHeader>
            <DialogTitle>Disattivare il sopralluogo secondario?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-700">
            Disattivando il sopralluogo secondario perderai i dati inseriti. Continuare?
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setConfirmDisableSecondary(false)}
              data-testid="confirm-disable-secondary-cancel"
            >
              Annulla
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                setForm({ ...form, allow_secondary_visit: false, secondary_visit_form_schema: null });
                setConfirmDisableSecondary(false);
              }}
              data-testid="confirm-disable-secondary-confirm"
            >
              Sì, disattiva
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0"><DialogTitle>{editing ? "Modifica" : "Nuovo"} Servizio</DialogTitle></DialogHeader>
          <form onSubmit={save} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 space-y-3">
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[color:hsl(var(--surface-alt))] border">
              <div>
                <Label className="font-semibold">Servizio attivo</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Se disattivato non compare nel marketplace</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })}
                  className="sr-only peer" data-testid="service-active-toggle" />
                <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>
            <div><Label>Nome</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="service-name" /></div>
            <div><Label>Slug</Label><Input required value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} /></div>
            <div><Label>Descrizione</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div>
              <Label>Informazioni per il cliente</Label>
              <Textarea
                value={form.client_info || ""}
                onChange={e => setForm({ ...form, client_info: e.target.value })}
                rows={4}
                placeholder="Cosa deve sapere il professionista/cliente prima di procedere? (documenti richiesti, tempi, cosa include, come ricevere il risultato...)"
                data-testid="svc-client-info"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Verrà mostrato in evidenza nel modal di acquisto del servizio.</p>
            </div>
            <div>
              <Label>Categoria</Label>
              <ServiceCategoryPicker
                value={form.category}
                onChange={(slug) => setForm({ ...form, category: slug })}
              />
            </div>
            <div>
              <Label>Icona</Label>
              <p className="text-xs text-muted-foreground mb-2">Seleziona un'icona rappresentativa del servizio</p>
              <div className="grid grid-cols-8 gap-2 max-h-48 overflow-y-auto p-2 border rounded" data-testid="icon-picker">
                {ICON_OPTIONS.map(name => {
                  const Ico = ICON_MAP[name];
                  const active = form.icon === name;
                  return (
                    <button key={name} type="button" onClick={() => setForm({ ...form, icon: name })}
                      title={name}
                      className={`aspect-square flex items-center justify-center rounded border transition-all hover:scale-105 ${
                        active ? "ht-gradient border-0 text-white ring-2 ring-purple-400" : "bg-white hover:border-purple-400"
                      }`}
                      data-testid={`icon-opt-${name}`}>
                      <Ico size={20} weight={active ? "fill" : "regular"} />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Prezzo base €</Label><Input type="number" step="0.01" required value={form.base_price} onChange={e => setForm({ ...form, base_price: e.target.value })} /></div>
              <div><Label>Consegna (giorni)</Label><Input type="number" value={form.delivery_time_days} onChange={e => setForm({ ...form, delivery_time_days: e.target.value })} /></div>
            </div>
            {/* Iter208.89 — Buffer prenotazione sopralluogo */}
            <div className="border rounded-md p-3 bg-slate-50/40">
              <Label>Buffer prenotazione sopralluogo (ore)</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Anticipo minimo in ore richiesto per prenotare un sopralluogo. Non si applica ai
                sopralluoghi aggiunti dagli operatori. Es. 48 = nessuna prenotazione nelle prossime 48h.
                Default 0 = nessun buffer.
              </p>
              <Input
                type="number"
                min="0"
                step="1"
                value={form.visit_booking_buffer_hours ?? 0}
                onChange={(e) => setForm({ ...form, visit_booking_buffer_hours: e.target.value })}
                placeholder="0"
                data-testid="service-visit-booking-buffer-hours"
                className="max-w-[160px]"
              />
            </div>
            <label className="flex items-start gap-2 p-3 rounded-md border cursor-pointer hover:bg-purple-50/40 transition" data-testid="fixed-price-toggle-row">
              <input
                type="checkbox"
                checked={!!form.fixed_price}
                onChange={(e) => setForm({ ...form, fixed_price: e.target.checked })}
                className="mt-0.5"
                data-testid="fixed-price-toggle"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">💎 Prezzo fisso (bypassa fasce di sconto)</div>
                <div className="text-xs text-slate-600">
                  Se attivo, il prezzo è uguale per tutti i professionisti e ignora la fascia di sconto assegnata (nessuna riduzione sul prezzo base).
                </div>
              </div>
            </label>
            <label className="flex items-start gap-2 p-3 rounded-md border cursor-pointer hover:bg-indigo-50/40 transition" data-testid="signature-requests-toggle-row">
              <input
                type="checkbox"
                checked={!!form.has_signature_requests}
                onChange={(e) => setForm({ ...form, has_signature_requests: e.target.checked })}
                className="mt-0.5"
                data-testid="signature-requests-toggle"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">Abilita invio documenti da firmare</div>
                <div className="text-xs text-slate-600">
                  Mostra nelle pratiche di questo servizio la sezione "Documenti da firmare" per inviare PDF in firma al cliente o al professionista.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-2 p-3 rounded-md border cursor-pointer hover:bg-blue-50/40 transition" data-testid="allow-secondary-visit-row">
              <input
                type="checkbox"
                checked={!!form.allow_secondary_visit}
                onChange={(e) => {
                  const next = e.target.checked;
                  if (next) {
                    // Iter207.8 — Auto-apri il form quando si attiva il toggle
                    setForm({
                      ...form,
                      allow_secondary_visit: true,
                      secondary_visit_form_schema: form.secondary_visit_form_schema || {
                        sections: [{
                          id: `sec_${Date.now()}`,
                          title: "Rilevamento sopralluogo secondario",
                          questions: [],
                        }],
                      },
                    });
                  } else if (hasSecondaryContent(form.secondary_visit_form_schema)) {
                    // Ha dati compilati → chiedi conferma prima di reset
                    setConfirmDisableSecondary(true);
                  } else {
                    // Nessun dato → disattiva subito e ripulisci
                    setForm({ ...form, allow_secondary_visit: false, secondary_visit_form_schema: null });
                  }
                }}
                className="mt-0.5"
                data-testid="allow-secondary-visit-toggle"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">📍 Permetti sopralluogo secondario</div>
                <div className="text-xs text-slate-600">
                  Se attivo, Desk/Tech (o Admin) possono iniettare uno step di sopralluogo aggiuntivo in corso d'opera dalla pratica. Al termine del sopralluogo la pratica torna automaticamente all'operatore che ha richiesto l'ispezione per la fase di consegna.
                </div>
              </div>
            </label>

            {/* Iter207.8 — Form del sopralluogo secondario (auto-aperto se toggle ON) */}
            {form.allow_secondary_visit && (
              <div className="p-3 rounded-md border border-blue-200 bg-blue-50/40 space-y-2" data-testid="secondary-visit-form-editor">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold text-blue-900">
                    📋 Form del sopralluogo secondario
                  </div>
                </div>
                <p className="text-[11px] text-blue-800/80">
                  Configura le domande che il site operator compilerà solo se e quando verrà richiesto un sopralluogo secondario su questa pratica. Se il secondario non viene richiesto, il form non compare mai.
                </p>
                <FormBuilder
                  schema={form.secondary_visit_form_schema || { sections: [{ id: `sec_${Date.now()}`, title: "Rilevamento sopralluogo secondario", questions: [] }] }}
                  onChange={(sch) => setForm({ ...form, secondary_visit_form_schema: sch })}
                  testIdPrefix="secondary-visit-form"
                />
              </div>
            )}
            {/* Iter72: quantità multipla */}
            <label className="flex items-start gap-2 p-3 rounded-md border cursor-pointer hover:bg-emerald-50/40 transition" data-testid="allow-multiple-quantity-row">
              <input
                type="checkbox"
                checked={!!form.allow_multiple_quantity}
                onChange={(e) => setForm({ ...form, allow_multiple_quantity: e.target.checked })}
                className="mt-0.5"
                data-testid="allow-multiple-quantity-toggle"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">🔢 Permetti quantità multipla</div>
                <div className="text-xs text-slate-600">
                  Se attivo, il PRO può acquistare più unità di questo servizio in un solo checkout (es. 3 APE per un portfolio). Verranno create N pratiche separate, ciascuna con il proprio addebito plafond e le proprie notifiche.
                </div>
              </div>
            </label>
            {/* Iter73: tier di sconto progressivo (visibile solo se qty multipla attiva) */}
            {form.allow_multiple_quantity && (
              <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50/40 space-y-2" data-testid="qty-discount-tiers-editor">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold text-emerald-900">💸 Sconto progressivo per quantità</div>
                    <div className="text-[11px] text-emerald-800/80">
                      Da quantità N in su, applica lo sconto % sul prezzo per pratica. Il tier con `min_qty` più alto vince (max 50%).
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm({
                      ...form,
                      quantity_discount_tiers: [...(form.quantity_discount_tiers || []), { min_qty: 3, discount_pct: 5 }],
                    })}
                    className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid="qty-discount-add"
                  >+ Tier</button>
                </div>
                {(form.quantity_discount_tiers || []).length === 0 && (
                  <div className="text-[11px] text-emerald-800/70 italic">
                    Nessuno sconto configurato. Aggiungi un tier per premiare gli acquisti multipli.
                  </div>
                )}
                {(form.quantity_discount_tiers || []).map((t, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`qty-discount-row-${idx}`}>
                    <label className="text-[11px] text-slate-700 whitespace-nowrap">Da</label>
                    <input
                      type="number" min="2" step="1"
                      value={t.min_qty ?? ""}
                      onChange={(e) => {
                        const arr = [...(form.quantity_discount_tiers || [])];
                        arr[idx] = { ...arr[idx], min_qty: e.target.value };
                        setForm({ ...form, quantity_discount_tiers: arr });
                      }}
                      className="w-16 h-8 border rounded px-2 text-sm"
                      data-testid={`qty-discount-min-qty-${idx}`}
                    />
                    <label className="text-[11px] text-slate-700 whitespace-nowrap">unità →</label>
                    <input
                      type="number" min="0" max="50" step="0.5"
                      value={t.discount_pct ?? ""}
                      onChange={(e) => {
                        const arr = [...(form.quantity_discount_tiers || [])];
                        arr[idx] = { ...arr[idx], discount_pct: e.target.value };
                        setForm({ ...form, quantity_discount_tiers: arr });
                      }}
                      className="w-16 h-8 border rounded px-2 text-sm"
                      data-testid={`qty-discount-pct-${idx}`}
                    />
                    <span className="text-[11px] text-slate-700">% di sconto</span>
                    <button
                      type="button"
                      onClick={() => {
                        const arr = (form.quantity_discount_tiers || []).filter((_, i) => i !== idx);
                        setForm({ ...form, quantity_discount_tiers: arr });
                      }}
                      className="ml-auto text-red-600 text-xs hover:text-red-800"
                      data-testid={`qty-discount-remove-${idx}`}
                      aria-label="Rimuovi tier"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
            {/* Iter63: Questionario cliente */}
            <label className="flex items-start gap-2 p-3 rounded-md border cursor-pointer hover:bg-purple-50/40 transition" data-testid="has-questionnaire-row">
              <input
                type="checkbox"
                checked={!!form.has_questionnaire}
                onChange={(e) => setForm({ ...form, has_questionnaire: e.target.checked })}
                className="mt-0.5"
                data-testid="has-questionnaire-toggle"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">❓ Attiva Questionario cliente</div>
                <div className="text-xs text-slate-600">
                  Se attivo, il cliente vedrà la domanda qui sotto nella pagina di pagamento e dovrà rispondere obbligatoriamente prima di procedere. La risposta sarà consultabile dagli operatori nella scheda pratica.
                </div>
                {form.has_questionnaire && (
                  <div className="mt-2 space-y-3">
                    <div>
                      <Label className="text-xs">Domanda principale (visualizzata come intestazione)</Label>
                      <textarea
                        value={form.question_text || ""}
                        onChange={(e) => setForm({ ...form, question_text: e.target.value })}
                        rows={2}
                        maxLength={1000}
                        className="w-full mt-1 border border-slate-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Es. Compila il questionario per procedere con l'ordine"
                        data-testid="question-text-input"
                      />
                      <div className="text-[10px] text-slate-500 text-right">
                        {(form.question_text || "").length}/1000
                      </div>
                    </div>
                    <QuestionnaireFormBuilder
                      fields={form.questionnaire_fields || []}
                      onChange={(next) => setForm({ ...form, questionnaire_fields: next })}
                    />
                  </div>
                )}
              </div>
            </label>
            {/* Iter79: Requisiti unificati (documenti + servizi concatenati in un'unica lista) */}
            <UnifiedRequirementsPicker
              form={form}
              setForm={setForm}
              services={services}
              documentCatalog={documentCatalog}
              editingId={editing?._id}
              onDocCatalogReload={reloadDocCatalog}
            />
            {/* Iter117: Prezzo Flessibile a bivi condizionali */}
            <div className="flex flex-col gap-2 p-3 rounded-md border" data-testid="flexible-price-section">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="has-flexible-price-row">
                <input
                  type="checkbox"
                  checked={!!form.has_flexible_price}
                  onChange={(e) => setForm({ ...form, has_flexible_price: e.target.checked })}
                  className="mt-0.5"
                  data-testid="has-flexible-price-toggle"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900">💰 Attiva Prezzo Flessibile</div>
                  <div className="text-xs text-slate-600">
                    Se attivo, in fase di acquisto il PRO risponde a una serie di domande (con eventuali bivi) che modificano il prezzo finale del servizio. Le risposte scelte saranno visibili agli operatori nella scheda pratica e nel PDF preventivo (senza mostrare gli aumenti applicati).
                  </div>
                </div>
              </label>
              {form.has_flexible_price && (
                <div className="mt-2 pl-6">
                  <PricingLogicEditor
                    value={form.pricing_logic || []}
                    onChange={(next) => setForm({ ...form, pricing_logic: next })}
                    servicesForClone={services}
                    currentServiceId={editing?._id}
                  />
                </div>
              )}
            </div>
            {/* Iter65: Salto sopralluogo */}
            <label className="flex items-start gap-2 p-3 rounded-md border cursor-pointer hover:bg-amber-50/40 transition" data-testid="can-skip-visit-row">
              <input
                type="checkbox"
                checked={!!form.can_skip_visit}
                onChange={(e) => setForm({ ...form, can_skip_visit: e.target.checked })}
                className="mt-0.5"
                data-testid="can-skip-visit-toggle"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">⏭️ Permetti salto sopralluogo</div>
                <div className="text-xs text-slate-600">
                  Se attivo, in fase di acquisto il Professionista può scegliere di saltare il sopralluogo. Verrà mostrato un avviso di responsabilità: Housetag e i tecnici non saranno responsabili per errori dovuti alla mancanza del sopralluogo. Attivalo solo su servizi in cui il sopralluogo è opzionale.
                </div>
              </div>
            </label>
            {form.can_skip_visit && (
              <div className="pl-3 -mt-1" data-testid="skip-visit-note-row">
                <label className="text-xs font-semibold text-slate-700">Nota salto sopralluogo <span className="font-normal text-slate-400">(facoltativa)</span></label>
                <Input
                  value={form.skip_visit_note || ""}
                  onChange={(e) => setForm({ ...form, skip_visit_note: e.target.value })}
                  placeholder="Es. Impianto termico non presente"
                  maxLength={300}
                  className="mt-1"
                  data-testid="skip-visit-note-input"
                />
              </div>
            )}
            {form.can_skip_visit && <SkipVisitPricingFields form={form} setForm={setForm} />}
            {/* Iter145: la Revisione Supervisore è ora per singolo step del workflow
                (vedi editor step più sotto). Il flag globale service-level è stato rimosso. */}
            {/* Iter140p: Split pagamenti anticipo/saldo */}
            <label className="flex items-start gap-2 p-3 rounded-md border cursor-pointer hover:bg-amber-50/40 transition" data-testid="split-payment-row">
              <input
                type="checkbox"
                checked={!!form.split_payment_enabled}
                onChange={(e) => setForm({ ...form, split_payment_enabled: e.target.checked })}
                className="mt-0.5"
                data-testid="split-payment-toggle"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">💰 Splitta pagamenti (Anticipo + Saldo)</div>
                <div className="text-xs text-slate-600 mb-2">
                  Se attivo, il cliente paga PRIMA una percentuale come anticipo per far partire la pratica; il saldo residuo alla consegna. L'IVA viene applicata proporzionalmente su entrambe le tranche.
                </div>
                {form.split_payment_enabled && (
                  <div className="flex items-center gap-2 mt-2" onClick={(e) => e.preventDefault()}>
                    <Label className="text-xs text-slate-700 font-medium">% Anticipo:</Label>
                    <Input
                      type="number"
                      min={1}
                      max={99}
                      value={form.advance_percentage ?? 50}
                      onChange={(e) => setForm({ ...form, advance_percentage: Math.max(1, Math.min(99, Number(e.target.value) || 0)) })}
                      className="w-20 h-8 text-sm"
                      data-testid="advance-percentage-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="text-sm text-slate-500">%</span>
                    <span className="text-[11px] text-slate-500 ml-2">
                      → Saldo: <strong>{100 - (Number(form.advance_percentage) || 0)}%</strong>
                    </span>
                  </div>
                )}
              </div>
            </label>
            <div><Label>Workflow</Label>
              <div className="text-xs text-muted-foreground mb-2">Il flusso di lavoro si configura nella sezione qui sotto — non serve più selezionarlo da una lista.</div>
            </div>
            <div className="border-t pt-3">
              <Label>Categorie professionista abilitate</Label>
              <p className="text-xs text-muted-foreground mb-2">Se non selezioni nulla, il servizio è visibile a tutte le categorie.</p>
              <div className="flex flex-wrap gap-2" data-testid="allowed-categories">
                {categories.map(c => {
                  const active = form.allowed_category_ids.includes(c._id);
                  return (
                    <button key={c._id} type="button" onClick={() => toggleCategory(c._id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active ? "ht-gradient border-0 text-white" : "bg-white hover:border-purple-400"
                      }`}
                      data-testid={`allowed-cat-${c._id}`}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
              {form.allowed_category_ids.length > 0 && (
                <p className="text-xs text-purple-700 mt-2">
                  Visibile solo a {form.allowed_category_ids.length}/{categories.length} categorie
                </p>
              )}
            </div>

            <div className="border-t pt-3" data-testid="service-visibility-section">
              <Label>Professionisti esclusi</Label>
              <label className="flex items-center gap-2 text-sm mt-1 mb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.visibility_scope === "selected"}
                  onChange={(e) => setForm({ ...form, visibility_scope: e.target.checked ? "selected" : "all" })}
                  data-testid="service-visibility-toggle"
                />
                Limita visibilità
              </label>
              {form.visibility_scope === "selected" ? (
                <>
                  <p className="text-xs text-muted-foreground mb-2">I professionisti selezionati (e i loro collaboratori) non vedranno né potranno acquistare questo servizio.</p>
                  <ProPicker
                    selected={form.hidden_professional_ids || []}
                    onChange={(ids) => setForm({ ...form, hidden_professional_ids: ids })}
                    testIdPrefix="service-hidden"
                  />
                </>
              ) : (
                <p className="text-xs text-muted-foreground">Il servizio è visibile a tutti i professionisti.</p>
              )}
            </div>

            {/* Parole chiave (keywords) */}
            <div className="border-t pt-3">
              <Label>Parole chiave</Label>
              <p className="text-xs text-muted-foreground mb-2">Aggiungi termini di ricerca (Invio o virgola per confermare). Verranno usate dai professionisti nel marketplace.</p>
              <div className="flex flex-wrap gap-2 mb-2" data-testid="keywords-list">
                {(form.keywords || []).map((k, i) => (
                  <span key={`${k}-${i}`} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs" data-testid={`kw-${k}`}>
                    {k}
                    <button type="button" onClick={() => setForm({ ...form, keywords: form.keywords.filter((_, ix) => ix !== i) })}
                      className="hover:text-red-600 ml-1" aria-label="rimuovi">×</button>
                  </span>
                ))}
              </div>
              <Input
                value={keywordInput}
                onChange={e => setKeywordInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    const val = keywordInput.trim().replace(",", "");
                    if (val && !(form.keywords || []).includes(val)) {
                      setForm({ ...form, keywords: [...(form.keywords || []), val] });
                    }
                    setKeywordInput("");
                  }
                }}
                placeholder="es. APE, energetica, casaclima"
                data-testid="keyword-input"
              />
            </div>

            {/* Iter79: Servizi concatenati e documenti sono ora unificati nella sezione "Requisiti" sopra */}

            {/* Iter208.68 — Azioni per il Pro (tasti custom nel modal pratica) */}
            <CustomActionsEditor
              actions={form.custom_actions || []}
              steps={workflowSteps}
              onChange={(next) => setForm({ ...form, custom_actions: next })}
            />

            {/* Operatori: punteggio e compenso per questo servizio */}
            <div className="border-t pt-3">
              <Label>Operatori: punteggio e compenso per questo servizio</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Punteggio da 1 a 5 (0 = escluso dall'assegnazione). Il compenso viene pagato agli operatori OUT quando completano una pratica di questo servizio: puoi scegliere una cifra fissa in € oppure una percentuale (%) calcolata sul totale della pratica.
              </p>
              {operatorScores.length === 0 ? (
                <div className="text-xs text-muted-foreground py-2 text-center border rounded">Nessun operatore approvato.</div>
              ) : (
                <div className="border rounded overflow-hidden max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[color:hsl(var(--surface-alt))] sticky top-0">
                      <tr className="text-left">
                        <th className="py-2 px-3">Operatore</th>
                        <th className="py-2 px-2">Ruolo</th>
                        <th className="py-2 px-2 text-center">Tipo</th>
                        <th className="py-2 px-2 text-center">Punteggio</th>
                        <th className="py-2 px-2 text-right pr-3">Compenso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {operatorScores.map((o, idx) => (
                        <tr key={o.operator_id} className="border-t" data-testid={`svc-op-row-${o.operator_id}`}>
                          <td className="py-2 px-3 font-medium">{o.first_name} {o.last_name}</td>
                          <td className="py-2 px-2 text-muted-foreground">{o.role.replace("_", " ")}</td>
                          <td className="py-2 px-2 text-center">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${o.operator_type === "out" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                              {o.operator_type?.toUpperCase() || "IN"}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-center">
                            <select
                              value={o.score}
                              onChange={e => {
                                const v = parseInt(e.target.value);
                                setOperatorScores(prev => prev.map((x, i) => i === idx ? { ...x, score: v } : x));
                              }}
                              className="border rounded h-7 px-1 text-xs"
                              data-testid={`svc-op-score-${o.operator_id}`}>
                              <option value={0}>0 (escluso)</option>
                              <option value={1}>1</option>
                              <option value={2}>2</option>
                              <option value={3}>3</option>
                              <option value={4}>4</option>
                              <option value={5}>5</option>
                            </select>
                          </td>
                          <td className="py-2 px-2 text-right pr-3">
                            <div className="inline-flex items-center gap-1">
                              <input type="number" min="0" step="0.01"
                                value={o.compensation}
                                onChange={e => {
                                  const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
                                  setOperatorScores(prev => prev.map((x, i) => i === idx ? { ...x, compensation: v } : x));
                                }}
                                disabled={o.operator_type !== "out"}
                                className="w-16 h-7 border rounded px-2 text-xs font-mono text-right disabled:bg-gray-50 disabled:text-gray-400"
                                data-testid={`svc-op-comp-${o.operator_id}`} />
                              <select
                                value={o.compensation_type || "eur"}
                                onChange={e => {
                                  const v = e.target.value;
                                  setOperatorScores(prev => prev.map((x, i) => i === idx ? { ...x, compensation_type: v } : x));
                                }}
                                disabled={o.operator_type !== "out"}
                                className="h-7 border rounded px-1 text-xs disabled:bg-gray-50 disabled:text-gray-400"
                                data-testid={`svc-op-comp-type-${o.operator_id}`}>
                                <option value="eur">€</option>
                                <option value="pct">%</option>
                              </select>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Flusso di lavoro (workflow inline) */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label>Flusso di lavoro</Label>
                  <p className="text-xs text-muted-foreground">Definisci gli step che compongono la lavorazione. Vengono eseguiti in ordine.</p>
                </div>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => {
                    const next = [...workflowSteps, {
                      id: crypto.randomUUID ? crypto.randomUUID() : `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                      name: "Nuovo Step",
                      assigned_role: "desk_operator",
                      order: workflowSteps.length + 1,
                      is_final: false,
                      is_visit: false,
                      visit_duration_minutes: 60,
                      description: "",
                      position_x: 80 + (workflowSteps.length % 4) * 240,
                      position_y: 80 + Math.floor(workflowSteps.length / 4) * 160,
                    }];
                    setWorkflowSteps(next);
                    setSelectedStepIdx(next.length - 1);
                  }}
                  data-testid="wf-add-step-btn">
                  <Plus size={14} weight="bold" className="mr-1" /> Aggiungi step
                </Button>
              </div>

              {workflowSteps.length === 0 ? (
                <div className="text-xs text-muted-foreground italic py-4 text-center border border-dashed rounded" data-testid="wf-empty">
                  Nessuno step. Aggiungi il primo step del flusso di lavoro.
                </div>
              ) : (() => {
                // Badge auto-calcolato: mostra se il servizio richiede sopralluogo e chi sceglie lo slot
                const visitIdx = workflowSteps.findIndex(s => s.is_visit);
                const hasVisit = visitIdx >= 0;
                const visitIsFirst = visitIdx === 0;
                return (
                  <div
                    className={`mb-3 rounded-md border-l-4 px-3 py-2 text-xs ${
                      hasVisit
                        ? "border-l-purple-500 bg-purple-50/60 text-purple-900"
                        : "border-l-slate-300 bg-slate-50 text-slate-600"
                    }`}
                    data-testid="visit-derived-badge"
                  >
                    {hasVisit ? (
                      <>
                        <div className="font-semibold">
                          🚗 Sopralluogo richiesto — determinato dallo step #{visitIdx + 1} «{workflowSteps[visitIdx].name || "Sopralluogo"}»
                          <span className="ml-1 text-[10px] font-mono bg-white/70 rounded px-1 py-0.5">
                            {workflowSteps[visitIdx].visit_duration_minutes || 60} min
                          </span>
                        </div>
                        <div className="mt-0.5 text-[11px] opacity-90">
                          {visitIsFirst
                            ? "In fase di acquisto il professionista sceglierà data e ora del sopralluogo."
                            : `L'operatore assegnato allo step #${visitIdx + 1} sceglierà data e ora del sopralluogo direttamente dalla pratica quando la riceverà.`}
                        </div>
                      </>
                    ) : (
                      <div>🚫 Nessun sopralluogo previsto — abilita la spunta "Sopralluogo" su uno step per attivarlo.</div>
                    )}
                  </div>
                );
              })()}
              {workflowSteps.length > 0 && (
                <div className="space-y-2" data-testid="wf-steps-list">
                  {workflowSteps.map((s, idx) => {
                    const expanded = selectedStepIdx === idx;
                    return (
                      <div key={s.id || idx}
                        className={`border rounded-md transition-all ${expanded ? "border-purple-400 bg-purple-50/30" : "hover:border-purple-200"}`}
                        data-testid={`wf-step-${idx}`}>
                        <div className="flex items-center gap-2 p-2">
                          <div className="flex flex-col">
                            <button type="button" disabled={idx === 0}
                              onClick={() => {
                                const arr = [...workflowSteps];
                                [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                                setWorkflowSteps(arr);
                                setSelectedStepIdx(idx - 1);
                              }}
                              className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                              data-testid={`wf-step-up-${idx}`}
                              aria-label="Sposta su">
                              <CaretUp size={14} weight="bold" />
                            </button>
                            <button type="button" disabled={idx === workflowSteps.length - 1}
                              onClick={() => {
                                const arr = [...workflowSteps];
                                [arr[idx + 1], arr[idx]] = [arr[idx], arr[idx + 1]];
                                setWorkflowSteps(arr);
                                setSelectedStepIdx(idx + 1);
                              }}
                              className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                              data-testid={`wf-step-down-${idx}`}
                              aria-label="Sposta giù">
                              <CaretDown size={14} weight="bold" />
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedStepIdx(expanded ? null : idx)}
                            className="flex-1 flex items-center gap-3 text-left"
                            data-testid={`wf-step-toggle-${idx}`}
                          >
                            <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{s.name || "Step senza nome"}</div>
                              <div className="text-[11px] text-muted-foreground truncate">
                                {(s.assigned_role || "").replace("_", " ")}
                                {s.is_visit ? " · 🚗 Sopralluogo" : ""}
                                {s.is_final ? " · 🏁 Finale" : ""}
                                {s.requires_approval ? " · 🔒 Approvazione" : ""}
                                {s.is_document_delivery ? " · 📎 Consegna" : ""}
                                {s.is_entity_fees_step ? " · 💶 Diritti ente" : ""}
                                {s.requires_supervision ? " · 🛡️ Supervisione" : ""}
                              </div>
                            </div>
                          </button>
                          <button type="button"
                            onClick={() => {
                              if (!window.confirm(`Eliminare lo step "${s.name}"?`)) return;
                              const arr = workflowSteps.filter((_, i) => i !== idx);
                              setWorkflowSteps(arr);
                              setSelectedStepIdx(null);
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                            data-testid={`wf-step-delete-${idx}`}
                            aria-label="Elimina step">
                            <Trash size={14} />
                          </button>
                        </div>
                        {expanded && (
                          <div className="border-t bg-white p-3 space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Nome step</Label>
                                <Input value={s.name || ""}
                                  onChange={e => setWorkflowSteps(prev => prev.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))}
                                  data-testid={`wf-step-name-${idx}`} />
                              </div>
                              <div>
                                <Label className="text-xs">Ruolo assegnato</Label>
                                <select
                                  className="w-full h-10 border rounded px-3 text-sm bg-white"
                                  value={s.assigned_role || "desk_operator"}
                                  onChange={e => {
                                    const newRole = e.target.value;
                                    setWorkflowSteps(prev => prev.map((x, i) => i === idx
                                      ? {
                                          ...x,
                                          assigned_role: newRole,
                                          // Vincolo bidirezionale: site_operator ⇔ is_visit
                                          is_visit: newRole === "site_operator",
                                          visit_duration_minutes: newRole === "site_operator" ? (x.visit_duration_minutes || 60) : x.visit_duration_minutes,
                                        }
                                      : x));
                                  }}
                                  disabled={!!s.is_visit}
                                  data-testid={`wf-step-role-${idx}`}
                                >
                                  <option value="desk_operator">{ROLE_LABELS.desk_operator}</option>
                                  <option value="site_operator">{ROLE_LABELS.site_operator}</option>
                                  <option value="tech_operator">{ROLE_LABELS.tech_operator}</option>
                                  <option value="accounting_operator">{ROLE_LABELS.accounting_operator}</option>
                                  <option value="admin">{ROLE_LABELS.admin}</option>
                                </select>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <label className="flex items-start gap-2 text-xs cursor-pointer">
                                <input type="checkbox" checked={!!s.is_visit}
                                  onChange={e => {
                                    const isV = e.target.checked;
                                    setWorkflowSteps(prev => prev.map((x, i) => i === idx
                                      ? {
                                          ...x,
                                          is_visit: isV,
                                          // Se attivo → forza site_operator; se disattivo → mantieni il ruolo attuale
                                          // (l'utente potrà cambiarlo dal select "Ruolo assegnato" senza dover eliminare lo step).
                                          assigned_role: isV ? "site_operator" : x.assigned_role,
                                          visit_duration_minutes: isV ? (x.visit_duration_minutes || 60) : x.visit_duration_minutes,
                                        }
                                      : x));
                                  }}
                                  data-testid={`wf-step-is-visit-${idx}`}
                                  className="mt-0.5" />
                                <span>Sopralluogo <span className="text-muted-foreground">(quando attivo, vincola al Site Operator)</span></span>
                              </label>
                              <label className="flex items-start gap-2 text-xs cursor-pointer">
                                <input type="checkbox" checked={!!s.is_final}
                                  onChange={e => setWorkflowSteps(prev => prev.map((x, i) => i === idx ? { ...x, is_final: e.target.checked } : x))}
                                  data-testid={`wf-step-final-${idx}`}
                                  className="mt-0.5" />
                                <span>Step finale <span className="text-muted-foreground">(abilita consegna documenti al pro)</span></span>
                              </label>
                              <label className="flex items-start gap-2 text-xs cursor-pointer col-span-2">
                                <input type="checkbox" checked={!!s.is_entity_fees_step}
                                  onChange={e => setWorkflowSteps(prev => prev.map((x, i) => i === idx ? { ...x, is_entity_fees_step: e.target.checked } : x))}
                                  data-testid={`wf-step-entity-fees-${idx}`}
                                  className="mt-0.5" />
                                <span>
                                  💶 <strong>Pagamento diritti ente</strong>{" "}
                                  <span className="text-muted-foreground">
                                    — l'operatore assegnato deve caricare bollettino (da pagare) o ricevuta
                                    (Housetag ha pagato). La pratica resta bloccata su questo step finché il
                                    professionista non salda.
                                  </span>
                                </span>
                              </label>
                              {/* Iter145: Revisione supervisore per singolo step */}
                              <label className="flex items-start gap-2 text-xs cursor-pointer col-span-2">
                                <input type="checkbox" checked={!!s.requires_supervision}
                                  onChange={e => setWorkflowSteps(prev => prev.map((x, i) => i === idx ? { ...x, requires_supervision: e.target.checked } : x))}
                                  data-testid={`wf-step-requires-supervision-${idx}`}
                                  className="mt-0.5" />
                                <span>
                                  🛡️ <strong>Richiede supervisione</strong>{" "}
                                  <span className="text-muted-foreground">
                                    — l'operatore assegnato a questo step vedrà il tasto "Richiedi Revisione".
                                    Avanzamento e consegna sono bloccati finché il supervisore assegnato non
                                    completa la verifica di questo step. Gli admin non possono richiedere revisione.
                                  </span>
                                </span>
                              </label>
                              <label className={`flex items-start gap-2 text-xs col-span-2 ${s.is_visit ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}>
                                <input type="checkbox" checked={!!s.has_form}
                                  disabled={!s.is_visit}
                                  onChange={e => setWorkflowSteps(prev => prev.map((x, i) => i === idx
                                    ? { ...x, has_form: e.target.checked, form_schema: e.target.checked ? (x.form_schema || { sections: [{ id: `sec_${Date.now()}`, title: "Rilevamento", questions: [] }] }) : x.form_schema }
                                    : x))}
                                  data-testid={`wf-step-has-form-${idx}`}
                                  className="mt-0.5" />
                                <span>
                                  📋 <strong>Form di sopralluogo</strong>{" "}
                                  <span className="text-muted-foreground">
                                    — l'operatore compila un form (testo, scelte, foto) prima di poter avanzare.
                                    {!s.is_visit && <em className="ml-1">Attivabile solo su step di Sopralluogo.</em>}
                                  </span>
                                </span>
                              </label>
                            </div>
                            {s.is_visit && s.has_form && (
                              <FormBuilder
                                schema={s.form_schema || { sections: [{ id: `sec_${Date.now()}`, title: "Rilevamento", questions: [] }] }}
                                onChange={(sch) => setWorkflowSteps(prev => prev.map((x, i) => i === idx ? { ...x, form_schema: sch } : x))}
                                testIdPrefix={`wf-step-form-${idx}`}
                              />
                            )}
                            {s.is_visit && (
                              <div>
                                <Label className="text-xs">Durata sopralluogo</Label>
                                <select className="w-full h-9 border rounded px-3 text-sm bg-white"
                                  value={s.visit_duration_minutes || 60}
                                  onChange={e => setWorkflowSteps(prev => prev.map((x, i) => i === idx ? { ...x, visit_duration_minutes: Number(e.target.value) } : x))}
                                  data-testid={`wf-step-visit-duration-${idx}`}>
                                  <option value={30}>30 minuti</option>
                                  <option value={60}>1 ora</option>
                                  <option value={90}>1 ora e 30 minuti</option>
                                </select>
                              </div>
                            )}
                            {/* Iter129 — Modalità Assegnazione: solo dal 2° step in poi.
                                'auto' (default) usa l'algoritmo di scoring;
                                'same_as' riusa l'operatore di uno step precedente
                                selezionabile dall'admin. I due campi vengono
                                inclusi nella funzione `save` più sopra. */}
                            {idx > 0 && (
                              <div className="mt-2 p-2 rounded bg-slate-50 border border-slate-200 space-y-2" data-testid={`wf-step-assignment-mode-block-${idx}`}>
                                <Label className="text-xs font-semibold text-slate-800">Modalità Assegnazione</Label>
                                <select
                                  className="w-full h-9 border rounded px-3 text-sm bg-white"
                                  value={s.assignment_mode || "auto"}
                                  onChange={e => setWorkflowSteps(prev => prev.map((x, i) => i === idx
                                    ? {
                                        ...x,
                                        assignment_mode: e.target.value,
                                        // se torniamo ad "auto", azzeriamo il riferimento allo step precedente
                                        same_as_step_id: e.target.value === "same_as" ? (x.same_as_step_id || "") : null,
                                      }
                                    : x))}
                                  data-testid={`wf-step-assignment-mode-${idx}`}
                                >
                                  <option value="auto">Automatico (punteggio)</option>
                                  <option value="same_as">Stesso operatore di uno step precedente</option>
                                </select>
                                {s.assignment_mode === "same_as" && (
                                  <div>
                                    <Label className="text-[11px] text-slate-600">Step di riferimento</Label>
                                    <select
                                      className="w-full h-9 border rounded px-3 text-sm bg-white"
                                      value={s.same_as_step_id || ""}
                                      onChange={e => setWorkflowSteps(prev => prev.map((x, i) => i === idx ? { ...x, same_as_step_id: e.target.value || null } : x))}
                                      data-testid={`wf-step-same-as-${idx}`}
                                    >
                                      <option value="">— Scegli lo step precedente —</option>
                                      {workflowSteps.slice(0, idx).map((prev, pIdx) => (
                                        <option key={prev.id || pIdx} value={prev.id || ""}>
                                          Step {pIdx + 1}: {prev.name || "senza nome"} ({prev.assigned_role || "?"})
                                        </option>
                                      ))}
                                    </select>
                                    {!s.same_as_step_id && (
                                      <div className="text-[10px] text-amber-700 mt-1">
                                        ⚠️ Devi selezionare uno step di riferimento, altrimenti il salvataggio userà "Automatico".
                                      </div>
                                    )}
                                    {/* Iter130 — Validazione ruolo compatibile. Il backend
                                        farà comunque fallback ad `auto` se i ruoli differiscono,
                                        ma qui avvisiamo l'admin prima del salvataggio. */}
                                    {s.same_as_step_id && (() => {
                                      const ref = workflowSteps.find(x => x.id === s.same_as_step_id);
                                      const refRole = ref?.assigned_role || "";
                                      const myRole = s.assigned_role || "";
                                      if (!refRole || !myRole) return null;
                                      if (refRole !== myRole) {
                                        return (
                                          <div
                                            className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-1"
                                            data-testid={`wf-step-same-as-role-mismatch-${idx}`}
                                          >
                                            ⚠️ Ruolo incompatibile: lo step selezionato è
                                            <strong> {refRole}</strong> mentre questo step richiede
                                            <strong> {myRole}</strong>. Se lasci questa configurazione,
                                            in fase di avanzamento il sistema tornerà in modalità
                                            "Automatico" e avviserà gli admin.
                                          </div>
                                        );
                                      }
                                      return (
                                        <div
                                          className="text-[11px] text-emerald-700 mt-1"
                                          data-testid={`wf-step-same-as-role-ok-${idx}`}
                                        >
                                          ✓ Ruoli compatibili ({myRole}).
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            </div>
            <div className="shrink-0 border-t bg-background px-6 py-4">
              <Button type="submit" className="w-full ht-gradient border-0" data-testid="svc-save-btn">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Iter66: Catalogo documenti globale */}
      <DocumentCatalogManager
        items={documentCatalog}
        onReload={load}
      />
    </div>
  );
}


// ==================== DocumentCatalogManager (Iter66) ====================
// Sezione dedicata sotto la tabella servizi: consente all'Admin di gestire
// l'elenco condiviso dei tipi di documento richiedibili dai servizi.
function DocumentCatalogManager({ items, onReload }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [busy, setBusy] = useState(null);

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy("add");
    try {
      await api.post("/document-catalog", {
        name: newName.trim(),
        description: newDesc.trim(),
        sort_order: (items || []).length,
        is_active: true,
      });
      setNewName(""); setNewDesc(""); setCreating(false);
      toast.success("Documento aggiunto");
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiunta documento");
    } finally { setBusy(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il documento dal catalogo? Verrà rimosso anche dai servizi che lo richiedono.")) return;
    setBusy(id);
    try {
      await api.delete(`/document-catalog/${id}`);
      toast.success("Documento eliminato");
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    } finally { setBusy(null); }
  };

  const toggleActive = async (item) => {
    setBusy(item._id);
    try {
      await api.patch(`/document-catalog/${item._id}`, {
        name: item.name, description: item.description || "",
        sort_order: item.sort_order || 0, is_active: !item.is_active,
      });
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento");
    } finally { setBusy(null); }
  };

  return (
    <div className="mt-10 kpi-card p-6" data-testid="document-catalog-manager">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-eyebrow">Impostazioni globali</div>
          <h2 className="font-display text-2xl font-bold">📄 Catalogo documenti</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Elenco condiviso dei tipi di documento richiedibili dai servizi.
            Ogni servizio può referenziare gli ID di questo catalogo.
          </p>
        </div>
        {!creating && (
          <Button onClick={() => setCreating(true)} className="ht-gradient border-0" data-testid="doc-catalog-add-btn">
            <Plus size={14} className="mr-1" /> Nuovo documento
          </Button>
        )}
      </div>

      {creating && (
        <form onSubmit={add} className="border rounded-md p-3 mb-3 bg-slate-50 space-y-2" data-testid="doc-catalog-add-form">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nome documento (es. Visura catastale)"
            maxLength={200}
            autoFocus
            data-testid="doc-catalog-add-name"
          />
          <Input
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Descrizione opzionale"
            maxLength={500}
            data-testid="doc-catalog-add-desc"
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy === "add" || !newName.trim()} data-testid="doc-catalog-add-save">
              {busy === "add" ? "Salvo…" : "Aggiungi"}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => { setCreating(false); setNewName(""); setNewDesc(""); }}>
              Annulla
            </Button>
          </div>
        </form>
      )}

      {(items || []).length === 0 ? (
        <div className="text-sm text-muted-foreground italic text-center py-6">
          Nessun documento nel catalogo. Aggiungi il primo per iniziare.
        </div>
      ) : (
        <div className="space-y-1">
          {items.map((d) => (
            <div
              key={d._id}
              className={`flex items-center gap-2 border rounded p-2 ${d.is_active ? "" : "bg-slate-50 opacity-70"}`}
              data-testid={`doc-catalog-item-${d._id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{d.name}</div>
                {d.description && <div className="text-xs text-slate-500 truncate">{d.description}</div>}
              </div>
              <label className="text-xs flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!d.is_active}
                  onChange={() => toggleActive(d)}
                  disabled={busy === d._id}
                  data-testid={`doc-catalog-item-${d._id}-active`}
                />
                Attivo
              </label>
              <button
                type="button"
                onClick={() => remove(d._id)}
                disabled={busy === d._id}
                className="text-red-600 text-xs hover:underline px-2"
                data-testid={`doc-catalog-item-${d._id}-delete`}
              >
                Elimina
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ==================== FORM BUILDER ====================
// Editor stile Google Forms per la compilazione di rilevamento sopralluogo.
// MVP: aggiunta/rimozione domande + tipi base (testo, numero, sì/no, scelta singola/multipla,
// foto). Branching + sezioni multiple in v2.
const QUESTION_TYPES = [
  { value: "short_text", label: "Testo breve" },
  { value: "long_text", label: "Testo lungo" },
  { value: "number", label: "Numero" },
  { value: "yes_no", label: "Sì / No" },
  { value: "single_choice", label: "Scelta singola" },
  { value: "multiple_choice", label: "Scelta multipla" },
  { value: "date", label: "Data" },
  { value: "photo_upload", label: "Foto (max 15)" },
];

function FormBuilder({ schema, onChange, testIdPrefix }) {
  const sections = schema?.sections || [];
  const section = sections[0] || { id: `sec_${Date.now()}`, title: "Rilevamento", questions: [] };
  const questions = section.questions || [];

  const update = (patch) => {
    const newSection = { ...section, ...patch };
    onChange({ ...schema, sections: [newSection, ...sections.slice(1)] });
  };

  const addQuestion = () => {
    const q = {
      id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
      type: "short_text", label: "Nuova domanda", required: false, options: [],
      // Iter140m — bivi genitore/figlia + foto per domanda + categoria
      category: "", enable_photo: false, parent_question_id: null, parent_answer_id: null,
    };
    update({ questions: [...questions, q] });
  };

  const patchQuestion = (idx, patch) => {
    const arr = questions.map((q, i) => (i === idx ? { ...q, ...patch } : q));
    update({ questions: arr });
  };

  const removeQuestion = (idx) => {
    // Se rimuoviamo una domanda-genitore, rimuoviamo anche il link `parent_question_id`
    // sui figli così non restano orfani con visibilità bloccata.
    const removedId = questions[idx]?.id;
    const rest = questions
      .filter((_, i) => i !== idx)
      .map(q => q.parent_question_id === removedId ? { ...q, parent_question_id: null, parent_answer_id: null } : q);
    update({ questions: rest });
  };

  // Iter168 — Riordino domande via freccette Su/Giù. Se lo spostamento porta
  // una domanda genitore DOPO una sua figlia (o una figlia PRIMA del genitore),
  // il link di bivio viene resettato per evitare riferimenti incoerenti.
  const moveQuestion = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= questions.length) return;
    const arr = [...questions];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    // Ricalcola coerenza parent → figlio in base al nuovo ordine.
    const pos = new Map(arr.map((q, i) => [q.id, i]));
    const cleaned = arr.map((q, i) => {
      if (q.parent_question_id && (pos.get(q.parent_question_id) ?? -1) >= i) {
        return { ...q, parent_question_id: null, parent_answer_id: null };
      }
      return q;
    });
    update({ questions: cleaned });
  };

  // Iter169 — Sensors DnD (drag-and-drop) per riordinare via mouse/touch.
  // Le freccette Su/Giù restano attive come alternativa a11y.
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = questions.findIndex(q => q.id === active.id);
    const newIdx = questions.findIndex(q => q.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(questions, oldIdx, newIdx);
    // Reset dei link parent → figlio che risultano incoerenti col nuovo ordine.
    const pos = new Map(reordered.map((q, i) => [q.id, i]));
    const cleaned = reordered.map((q, i) => {
      if (q.parent_question_id && (pos.get(q.parent_question_id) ?? -1) >= i) {
        return { ...q, parent_question_id: null, parent_answer_id: null };
      }
      return q;
    });
    update({ questions: cleaned });
  };

  const needsOptions = (t) => t === "single_choice" || t === "multiple_choice";
  const isChoice = (t) => t === "single_choice" || t === "multiple_choice" || t === "yes_no";

  return (
    <div className="border rounded-md bg-slate-50/50 p-3 mt-2" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between mb-2">
        <input
          type="text" value={section.title}
          onChange={(e) => update({ title: e.target.value })}
          placeholder="Titolo sezione"
          className="border rounded px-2 py-1 text-sm bg-white flex-1 mr-2"
          data-testid={`${testIdPrefix}-title`}
        />
        <button type="button" onClick={addQuestion}
          className="text-xs px-3 py-1 rounded bg-purple-600 text-white hover:bg-purple-700"
          data-testid={`${testIdPrefix}-add-q`}>
          + Domanda
        </button>
      </div>
      {questions.length === 0 ? (
        <div className="text-xs text-muted-foreground italic text-center py-4">
          Nessuna domanda. Clicca "+ Domanda" per iniziare.
        </div>
      ) : (
        <div className="space-y-2">
          <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={questions.map(q => q.id)} strategy={verticalListSortingStrategy}>
              {questions.map((q, idx) => {
            // Iter140m: candidati genitore = domande PRECEDENTI (evita loop).
            const parentCandidates = questions.slice(0, idx);
            const parentQ = q.parent_question_id ? questions.find(x => x.id === q.parent_question_id) : null;
            // Se il parent è a scelta multipla/singola/yes-no, mostra le opzioni;
            // altrimenti (testo/numero/data) opzione singola "quando compilata".
            const parentIsChoice = parentQ && isChoice(parentQ.type);
            const parentOptions = parentQ?.type === "yes_no"
              ? [{ id: "Sì", label: "Sì" }, { id: "No", label: "No" }]
              : (parentQ?.options || []);
            return (
              <SortableQuestionRow key={q.id} id={q.id} testIdPrefix={testIdPrefix} idx={idx}>
                {({ attributes, listeners }) => (
              <div className="bg-white border rounded p-2" data-testid={`${testIdPrefix}-q-${idx}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {/* Iter169 — Maniglia drag-and-drop. Le freccette restano come fallback a11y. */}
                  <button
                    type="button"
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-purple-700 px-1 py-0.5 rounded hover:bg-purple-50"
                    title="Trascina per riordinare"
                    aria-label="Trascina domanda per riordinare"
                    data-testid={`${testIdPrefix}-q-${idx}-drag-handle`}
                  >
                    <DotsSixVertical size={14} weight="bold" />
                  </button>
                  <span className="text-[10px] font-mono bg-slate-100 rounded px-1.5 py-0.5">#{idx + 1}</span>
                  {/* Iter168 — Riordino via freccette. Disabilitate agli estremi. */}
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveQuestion(idx, -1)}
                      disabled={idx === 0}
                      className="text-slate-500 hover:text-purple-700 disabled:opacity-30 disabled:cursor-not-allowed px-1 py-0.5 rounded hover:bg-purple-50"
                      title="Sposta su"
                      aria-label="Sposta domanda su"
                      data-testid={`${testIdPrefix}-q-${idx}-move-up`}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      onClick={() => moveQuestion(idx, +1)}
                      disabled={idx === questions.length - 1}
                      className="text-slate-500 hover:text-purple-700 disabled:opacity-30 disabled:cursor-not-allowed px-1 py-0.5 rounded hover:bg-purple-50"
                      title="Sposta giù"
                      aria-label="Sposta domanda giù"
                      data-testid={`${testIdPrefix}-q-${idx}-move-down`}
                    >
                      ▼
                    </button>
                  </div>
                  <select
                    value={q.type}
                    onChange={(e) => patchQuestion(idx, { type: e.target.value, options: needsOptions(e.target.value) ? (q.options?.length ? q.options : [{ id: `opt_${Date.now()}`, label: "Opzione 1" }]) : [] })}
                    className="border rounded px-2 py-1 text-xs bg-white"
                    data-testid={`${testIdPrefix}-q-${idx}-type`}>
                    {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <label className="text-[11px] flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={!!q.required}
                      onChange={(e) => patchQuestion(idx, { required: e.target.checked })}
                      data-testid={`${testIdPrefix}-q-${idx}-required`} />
                    Obbl.
                  </label>
                  {/* Iter140m: toggle foto per questa domanda */}
                  <label className="text-[11px] flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={!!q.enable_photo}
                      onChange={(e) => patchQuestion(idx, { enable_photo: e.target.checked })}
                      data-testid={`${testIdPrefix}-q-${idx}-enable-photo`} />
                    📷 Foto
                  </label>
                  <button type="button" onClick={() => removeQuestion(idx)}
                    className="ml-auto text-xs text-red-600 hover:underline"
                    data-testid={`${testIdPrefix}-q-${idx}-remove`}>
                    Rimuovi
                  </button>
                </div>
                <input type="text" value={q.label}
                  onChange={(e) => patchQuestion(idx, { label: e.target.value })}
                  placeholder="Testo della domanda"
                  className="w-full border rounded px-2 py-1 text-sm"
                  data-testid={`${testIdPrefix}-q-${idx}-label`} />
                {/* Iter140m: categoria (raggruppamento accordion in fase di compilazione) */}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <label className="text-[11px] text-slate-600 flex items-center gap-1 flex-1 min-w-[180px]">
                    Categoria:
                    <input type="text" value={q.category || ""}
                      onChange={(e) => patchQuestion(idx, { category: e.target.value })}
                      placeholder="es. Impianti, Esterni"
                      className="flex-1 border rounded px-2 py-1 text-xs"
                      data-testid={`${testIdPrefix}-q-${idx}-category`} />
                  </label>
                  {/* Iter140m: bivio Genitore → mostra questa domanda solo se... */}
                  {parentCandidates.length > 0 && (
                    <>
                      <label className="text-[11px] text-slate-600 flex items-center gap-1">
                        Mostra se:
                        <select
                          value={q.parent_question_id || ""}
                          onChange={(e) => patchQuestion(idx, { parent_question_id: e.target.value || null, parent_answer_id: null })}
                          className="border rounded px-2 py-1 text-xs bg-white max-w-[180px]"
                          data-testid={`${testIdPrefix}-q-${idx}-parent-q`}>
                          <option value="">— sempre visibile —</option>
                          {parentCandidates.map((pq, pi) => (
                            <option key={pq.id} value={pq.id}>#{pi + 1} {pq.label?.slice(0, 30) || pq.id}</option>
                          ))}
                        </select>
                      </label>
                      {q.parent_question_id && parentIsChoice && (
                        <label className="text-[11px] text-slate-600 flex items-center gap-1">
                          risposta:
                          <select
                            value={q.parent_answer_id || ""}
                            onChange={(e) => patchQuestion(idx, { parent_answer_id: e.target.value || null })}
                            className="border rounded px-2 py-1 text-xs bg-white max-w-[180px]"
                            data-testid={`${testIdPrefix}-q-${idx}-parent-a`}>
                            <option value="">— qualsiasi —</option>
                            {parentOptions.map(opt => (
                              <option key={opt.id} value={opt.id}>{opt.label}</option>
                            ))}
                          </select>
                        </label>
                      )}
                      {q.parent_question_id && !parentIsChoice && (
                        <span className="text-[10px] text-slate-500 italic">quando compilata</span>
                      )}
                    </>
                  )}
                </div>
                {needsOptions(q.type) && (
                  <div className="mt-2 space-y-1">
                    {(q.options || []).map((opt, oi) => (
                      <div key={opt.id} className="flex items-center gap-1">
                        <input type="text" value={opt.label}
                          onChange={(e) => patchQuestion(idx, { options: q.options.map((o, i) => i === oi ? { ...o, label: e.target.value } : o) })}
                          placeholder={`Opzione ${oi + 1}`}
                          className="flex-1 border rounded px-2 py-1 text-xs" />
                        <button type="button"
                          onClick={() => patchQuestion(idx, { options: q.options.filter((_, i) => i !== oi) })}
                          className="text-red-500 text-xs px-1">×</button>
                      </div>
                    ))}
                    <button type="button"
                      onClick={() => patchQuestion(idx, { options: [...(q.options || []), { id: `opt_${Date.now()}`, label: `Opzione ${(q.options?.length || 0) + 1}` }] })}
                      className="text-xs text-purple-600 hover:underline">
                      + Opzione
                    </button>
                  </div>
                )}
              </div>
                )}
              </SortableQuestionRow>
            );
          })}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}

// Iter169 — Sortable wrapper per le righe del FormBuilder. Applica ref/transform
// del dnd-kit e fornisce `attributes`/`listeners` al render-prop così la maniglia
// (`DotsSixVertical`) può essere posizionata dentro l'header della card.
function SortableQuestionRow({ id, testIdPrefix, idx, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`${testIdPrefix}-q-${idx}-sortable`}
    >
      {typeof children === "function" ? children({ attributes, listeners, isDragging }) : children}
    </div>
  );
}



// ==================== QuestionnaireFormBuilder (Iter64) ====================
// Builder Google-Forms style per il questionario cliente. Gestisce una lista
// flat di field {id, label, type, required, options: string[]}.
const Q_TYPES = [
  { value: "text", label: "Testo breve" },
  { value: "textarea", label: "Testo lungo" },
  { value: "radio", label: "Scelta singola" },
  { value: "checkbox", label: "Scelta multipla" },
  { value: "select", label: "Menu a tendina" },
];
const Q_NEEDS_OPTIONS = (t) => t === "radio" || t === "checkbox" || t === "select";

function QuestionnaireFormBuilder({ fields, onChange }) {
  const patch = (idx, part) => onChange(fields.map((f, i) => (i === idx ? { ...f, ...part } : f)));
  const add = () => onChange([
    ...fields,
    {
      id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: "Nuova domanda",
      type: "text",
      required: false,
      options: [],
    },
  ]);
  const remove = (idx) => onChange(fields.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const next = [...fields];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="border rounded-md bg-slate-50/60 p-3" data-testid="questionnaire-builder">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider font-bold text-purple-800">
          Domande del questionario
        </div>
        <button
          type="button"
          onClick={add}
          className="text-xs px-3 py-1 rounded bg-purple-600 text-white hover:bg-purple-700"
          data-testid="questionnaire-builder-add"
        >
          + Domanda
        </button>
      </div>
      {fields.length === 0 ? (
        <div className="text-xs text-muted-foreground italic text-center py-4">
          Nessuna domanda. Se non aggiungi domande, verrà mostrata solo la
          "domanda principale" con una textarea singola.
        </div>
      ) : (
        <div className="space-y-2">
          {fields.map((f, idx) => (
            <div key={f.id} className="bg-white border rounded p-2" data-testid={`qb-q-${idx}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono bg-slate-100 rounded px-1.5 py-0.5">#{idx + 1}</span>
                <select
                  value={f.type}
                  onChange={(e) => patch(idx, {
                    type: e.target.value,
                    options: Q_NEEDS_OPTIONS(e.target.value)
                      ? (f.options?.length ? f.options : ["Opzione 1"])
                      : [],
                  })}
                  className="border rounded px-2 py-1 text-xs bg-white"
                  data-testid={`qb-q-${idx}-type`}
                >
                  {Q_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <label className="text-[11px] flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!f.required}
                    onChange={(e) => patch(idx, { required: e.target.checked })}
                    data-testid={`qb-q-${idx}-required`}
                  />
                  Obbl.
                </label>
                <div className="ml-auto flex items-center gap-1">
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                    className="text-xs px-1.5 py-0.5 rounded border disabled:opacity-30" title="Sposta su">↑</button>
                  <button type="button" onClick={() => move(idx, +1)} disabled={idx === fields.length - 1}
                    className="text-xs px-1.5 py-0.5 rounded border disabled:opacity-30" title="Sposta giù">↓</button>
                  <button type="button" onClick={() => remove(idx)}
                    className="text-xs text-red-600 hover:underline ml-1"
                    data-testid={`qb-q-${idx}-remove`}>Rimuovi</button>
                </div>
              </div>
              <input
                type="text"
                value={f.label}
                onChange={(e) => patch(idx, { label: e.target.value })}
                placeholder="Testo della domanda (visibile al cliente)"
                className="w-full border rounded px-2 py-1 text-sm"
                maxLength={500}
                data-testid={`qb-q-${idx}-label`}
              />
              {Q_NEEDS_OPTIONS(f.type) && (
                <div className="mt-2 space-y-1">
                  {(f.options || []).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-1">
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => patch(idx, { options: (f.options || []).map((o, i) => i === oi ? e.target.value : o) })}
                        placeholder={`Opzione ${oi + 1}`}
                        className="flex-1 border rounded px-2 py-1 text-xs"
                        maxLength={200}
                        data-testid={`qb-q-${idx}-opt-${oi}`}
                      />
                      <button
                        type="button"
                        onClick={() => patch(idx, { options: (f.options || []).filter((_, i) => i !== oi) })}
                        className="text-red-500 text-xs px-1"
                        title="Rimuovi opzione"
                      >×</button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => patch(idx, { options: [...(f.options || []), `Opzione ${(f.options?.length || 0) + 1}`] })}
                    className="text-xs text-purple-600 hover:underline"
                    data-testid={`qb-q-${idx}-add-opt`}
                  >
                    + Opzione
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== ServiceCategoryPicker (Iter208.62) ====================
// Dropdown custom con:
// - elenco categorie da /api/service-categories
// - "+ Nuova categoria" inline con Invio per salvare
// - matita (rename inline) + cestino (elimina) per ogni voce
function ServiceCategoryPicker({ value, onChange }) {
  const [cats, setCats] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [newMode, setNewMode] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  const [renamingId, setRenamingId] = React.useState(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const rootRef = React.useRef(null);
  const newInputRef = React.useRef(null);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/service-categories");
      setCats(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore lettura categorie");
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) {
        setOpen(false); setNewMode(false); setRenamingId(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  React.useEffect(() => {
    if (newMode && newInputRef.current) newInputRef.current.focus();
  }, [newMode]);

  const currentLabel = React.useMemo(() => {
    const found = cats.find((c) => c.slug === value);
    return found ? found.name : (value || "— Seleziona categoria —");
  }, [cats, value]);

  const createCat = async () => {
    const name = newName.trim();
    if (!name || saving) return;
    setSaving(true);
    try {
      const r = await api.post("/service-categories", { name });
      await load();
      onChange?.(r.data.slug);
      setNewMode(false);
      setNewName("");
      toast.success("Categoria creata");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    } finally { setSaving(false); }
  };

  const renameCat = async (cid) => {
    const name = renameValue.trim();
    if (!name || saving) return;
    const cur = cats.find((x) => x._id === cid);
    if (cur && cur.name === name) { setRenamingId(null); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/service-categories/${cid}`, { name });
      await load();
      // Se il servizio corrente usa lo slug rinominato, aggiorna la selezione
      if (cur && value === cur.slug) onChange?.(r.data.slug);
      setRenamingId(null);
      toast.success("Categoria rinominata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rinomina");
    } finally { setSaving(false); }
  };

  const deleteCat = async (cid, cat) => {
    if (cat.usage_count > 0) {
      toast.error(`Impossibile eliminare: ${cat.usage_count} servizi attivi la usano`);
      return;
    }
    if (!confirm(`Eliminare la categoria "${cat.name}"?`)) return;
    try {
      await api.delete(`/service-categories/${cid}`);
      await load();
      if (value === cat.slug) onChange?.("");
      toast.success("Categoria eliminata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div className="relative" ref={rootRef} data-testid="service-category-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm text-left flex items-center justify-between"
        data-testid="service-category-trigger"
      >
        <span className={value ? "text-slate-900" : "text-slate-400"}>{currentLabel}</span>
        <CaretDown size={14} className="text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid="service-category-dropdown">
          {cats.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">Nessuna categoria. Creane una qui sotto.</div>
          )}
          <ul className="divide-y">
            {cats.map((c) => {
              const isRenaming = renamingId === c._id;
              const active = c.slug === value;
              return (
                <li key={c._id} className={"group flex items-center gap-1 px-2 py-1.5 hover:bg-slate-50 " + (active ? "bg-purple-50" : "")}>
                  {isRenaming ? (
                    <>
                      <Input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); renameCat(c._id); }
                          if (e.key === "Escape") { setRenamingId(null); }
                        }}
                        className="h-8 flex-1 text-sm"
                        data-testid={`cat-rename-input-${c._id}`}
                      />
                      <button
                        type="button"
                        onClick={() => renameCat(c._id)}
                        className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                        title="Salva"
                        data-testid={`cat-rename-save-${c._id}`}
                      ><CheckCircle size={14} weight="fill" /></button>
                      <button
                        type="button"
                        onClick={() => setRenamingId(null)}
                        className="p-1 text-slate-500 hover:bg-slate-100 rounded"
                        title="Annulla"
                      ><Warning size={14} /></button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => { onChange?.(c.slug); setOpen(false); }}
                        className="flex-1 min-w-0 text-left text-sm py-0.5 truncate"
                        data-testid={`cat-select-${c._id}`}
                        title={`Slug: ${c.slug} · ${c.usage_count} servizi attivi`}
                      >
                        {c.name}
                        {c.usage_count > 0 && (
                          <span className="ml-2 text-[10px] text-slate-400">({c.usage_count})</span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setRenamingId(c._id); setRenameValue(c.name); }}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-100 text-slate-500 transition-opacity"
                        title="Rinomina"
                        data-testid={`cat-edit-${c._id}`}
                      ><Pencil size={12} /></button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteCat(c._id, c); }}
                        disabled={c.usage_count > 0}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-red-600 disabled:text-slate-300 disabled:cursor-not-allowed transition-opacity"
                        title={c.usage_count > 0 ? "In uso da servizi attivi" : "Elimina"}
                        data-testid={`cat-delete-${c._id}`}
                      ><Trash size={12} /></button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="border-t bg-slate-50">
            {newMode ? (
              <div className="flex items-center gap-1 px-2 py-1.5">
                <Input
                  ref={newInputRef}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); createCat(); }
                    if (e.key === "Escape") { setNewMode(false); setNewName(""); }
                  }}
                  placeholder="Nome nuova categoria…"
                  className="h-8 flex-1 text-sm"
                  data-testid="cat-new-input"
                />
                <button
                  type="button"
                  onClick={createCat}
                  disabled={saving || !newName.trim()}
                  className="p-1 text-emerald-600 hover:bg-emerald-100 rounded disabled:opacity-40"
                  title="Crea"
                  data-testid="cat-new-save"
                ><CheckCircle size={14} weight="fill" /></button>
                <button
                  type="button"
                  onClick={() => { setNewMode(false); setNewName(""); }}
                  className="p-1 text-slate-500 hover:bg-slate-100 rounded"
                  title="Annulla"
                ><Warning size={14} /></button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setNewMode(true)}
                className="w-full px-3 py-2 text-left text-sm text-purple-700 hover:bg-purple-50 font-semibold flex items-center gap-1"
                data-testid="cat-new-toggle"
              >
                <Plus size={13} weight="bold" /> Nuova categoria
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

